import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { AUTH_ENDPOINTS } from '../../globals/app-endpoints';
import { environment } from 'src/environments/environments';

/**
 * URLs that must never trigger an automatic token-refresh attempt.
 * Auth endpoints are excluded to avoid infinite loops.
 */
const SKIP_REFRESH_URLS = [
  `${environment.api_url}${AUTH_ENDPOINTS.LOGIN}`,
  `${environment.api_url}${AUTH_ENDPOINTS.REFRESH_TOKEN}`,
  `${environment.api_url}${AUTH_ENDPOINTS.GOOGLE_LOGIN}`,
  `${environment.api_url}${AUTH_ENDPOINTS.LOGOUT}`,
];

export const tokenRefreshInterceptor: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  const authService = inject(AuthService);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      const isRefreshSkipped = SKIP_REFRESH_URLS.some((url) =>
        request.url.startsWith(url),
      );
      if (error.status === 403 && !isRefreshSkipped) {
        authService.logout();
        return throwError(() => error);
      }
      if (error.status === 401 && !isRefreshSkipped) {
        return authService.refreshAccessToken().pipe(
          switchMap((newToken: string) => {
            // Retry the original request with the fresh access token
            const retried = request.clone({
              setHeaders: { Authorization: `Bearer ${newToken}` },
            });
            return next(retried);
          }),
          catchError((refreshError) => throwError(() => refreshError)),
        );
      }

      return throwError(() => error);
    }),
  );
};
